import React, { useEffect, useId, useState } from "react";
import { ArrowDown, ArrowUp, Save, Trash2 } from "lucide-react";
import { assessmentTasks, taskEvidenceRequirements } from "@inclusivecore/shared";

const errorText = (e) => e?.message || "The request failed. Try again.";

function TaskEvidenceChoices({ question, onChange, disabled = false }) {
  const id = useId();
  const tasks = assessmentTasks(question);
  const choices = taskEvidenceRequirements(question);
  return <fieldset className="space-y-3 rounded-lg border border-border p-3">
    <legend className="px-1 text-sm font-semibold">Evidence requirements</legend>
    <p className="text-sm text-muted-foreground">Choose for every task before publishing. Changing the question or task text clears these choices for confirmation.</p>
    {tasks.map((task, index) => <div key={index}>
      <label htmlFor={`${id}-${index}`} className="block text-sm font-semibold">Evidence for task {index + 1}: {task || 'Question text'}</label>
      <select id={`${id}-${index}`} value={choices[index]} disabled={disabled} onChange={event => onChange(choices.map((value, position) => position === index ? event.target.value : value))} className="mt-1 min-h-11 w-full rounded-lg border border-input bg-background px-3 text-foreground">
        <option value="unset">Not set</option><option value="required">Required</option><option value="optional">Optional</option>
      </select>
    </div>)}
  </fieldset>;
}

function QuestionCard({
  question,
  index,
  group,
  readOnly,
  onSave,
  onDelete,
  onMove,
  onDirtyChange,
  globalBusy = false,
  reorderDisabled = false,
}) {
  const [draft, setDraft] = useState({
    ...question,
    sub_criteria: question.sub_criteria || [],
  });
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    onDirtyChange(question.id, dirty);
    return () => onDirtyChange(question.id, false);
  }, [question.id, dirty, onDirtyChange]);
  useEffect(() => {
    if (readOnly) {
      setEditing(false);
      setDirty(false);
    }
  }, [readOnly]);
  useEffect(() => {
    if (!dirty)
      setDraft({ ...question, sub_criteria: question.sub_criteria || [] });
  }, [question, dirty]);
  async function save() {
    setBusy(true);
    setError("");
    try {
      const result = await onSave(question.id, {
        level: Number(draft.level),
        theme: draft.theme,
        question_text: draft.question_text,
        help_text: draft.help_text || "",
        order: Number(draft.order),
        weight: Number(draft.weight),
        sub_criteria:
          typeof draft.sub_criteria === "string"
            ? draft.sub_criteria
                .split("\n")
                .map((item) => item.trim())
                .filter(Boolean)
            : draft.sub_criteria,
        task_evidence_requirements: draft.task_evidence_requirements || [],
      });
      if (result) {
        setEditing(false);
        setDirty(false);
      }
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  async function remove() {
    const confirmation = window.prompt(
      `Type DELETE to remove “${question.question_text}”. Historical questions cannot be removed.`,
    );
    if (confirmation !== "DELETE") return;
    setBusy(true);
    try {
      await onDelete(question.id);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  const update = (key, value) => {
    setDirty(true);
    setDraft((d) => ({ ...d, [key]: value, ...(['question_text', 'sub_criteria'].includes(key) ? { task_evidence_requirements: [] } : {}) }));
  };
  const criteriaText =
    typeof draft.sub_criteria === "string"
      ? draft.sub_criteria
      : (draft.sub_criteria || []).join("\n");
  const evidenceQuestion = { ...draft, sub_criteria: criteriaText.split('\n').map(item => item.trim()).filter(Boolean) };
  return (
    <article
      className="rounded-xl border border-border bg-card p-4"
      aria-label={`Question ${index + 1}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-foreground">
            Level {question.level} · {question.theme} · order {question.order}
          </p>
          {editing ? (
            <div className="mt-3 space-y-3">
              <label className="block text-sm font-semibold">
                Question text
                <textarea
                  disabled={busy || globalBusy}
                  value={draft.question_text}
                  onChange={(e) => update("question_text", e.target.value)}
                  rows={3}
                  className="mt-1 w-full rounded-lg border border-border p-3"
                />
              </label>
              <div className="grid gap-3 sm:grid-cols-3">
                <label className="text-sm font-semibold">
                  Level
                  <input
                    disabled={busy || globalBusy}
                    type="number"
                    min="1"
                    max="3"
                    value={draft.level}
                    onChange={(e) => update("level", e.target.value)}
                    className="mt-1 min-h-11 w-full rounded-lg border px-2"
                  />
                </label>
                <label className="text-sm font-semibold">
                  Theme
                  <input
                    disabled={busy || globalBusy}
                    value={draft.theme}
                    onChange={(e) => update("theme", e.target.value)}
                    className="mt-1 min-h-11 w-full rounded-lg border px-2"
                  />
                </label>
                <label className="text-sm font-semibold">
                  Weight
                  <input
                    disabled={busy || globalBusy}
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={draft.weight}
                    onChange={(e) => update("weight", e.target.value)}
                    className="mt-1 min-h-11 w-full rounded-lg border px-2"
                  />
                </label>
              </div>
              <label className="block text-sm font-semibold">
                Help text
                <textarea
                  disabled={busy || globalBusy}
                  value={draft.help_text || ""}
                  onChange={(e) => update("help_text", e.target.value)}
                  rows={2}
                  className="mt-1 w-full rounded-lg border border-border p-3"
                />
              </label>
              <label className="block text-sm font-semibold">
                Sub-criteria (one per line)
                <textarea
                  disabled={busy || globalBusy}
                  value={criteriaText}
                  onChange={(e) => update("sub_criteria", e.target.value)}
                  rows={3}
                  className="mt-1 w-full rounded-lg border border-border p-3"
                />
              </label>
              <TaskEvidenceChoices question={evidenceQuestion} disabled={busy || globalBusy} onChange={choices => update('task_evidence_requirements', choices)} />
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={busy || globalBusy}
                  onClick={save}
                  className="min-h-11 rounded-lg bg-teal-700 px-3 text-sm font-semibold text-white"
                >
                  <Save className="mr-1 inline h-4 w-4" aria-hidden="true" />
                  Save
                </button>
                <button
                  type="button"
                  disabled={busy || globalBusy}
                  onClick={() => {
                    setEditing(false);
                    setDirty(false);
                  }}
                  className="min-h-11 rounded-lg border px-3 text-sm font-semibold"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <p className="mt-2 whitespace-pre-wrap text-sm text-foreground">
              {question.question_text}
            </p>
          )}
          {question.help_text && !editing && (
            <p className="mt-2 text-xs text-muted-foreground">
              {question.help_text}
            </p>
          )}
          {!editing && <ul className="mt-3 space-y-1 text-sm text-foreground">
            {assessmentTasks(question).map((task, position) => <li key={position}>Task {position + 1}: {task}. Evidence {taskEvidenceRequirements(question)[position] === 'unset' ? 'not set' : taskEvidenceRequirements(question)[position]}.</li>)}
          </ul>}
          {error && (
            <p role="alert" className="mt-2 text-sm text-red-700">
              {error}
            </p>
          )}
        </div>
        <div className="flex shrink-0 flex-col justify-end gap-1 sm:flex-row sm:flex-wrap">
          <button
            type="button"
            disabled={readOnly || busy || globalBusy || reorderDisabled || index === 0}
            onClick={() => onMove(question.id, group, -1)}
            aria-label="Move question up"
            className="min-h-11 rounded-md border p-2 disabled:opacity-40"
          >
            <ArrowUp className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            disabled={
              readOnly || busy || globalBusy || reorderDisabled || index === group.length - 1
            }
            onClick={() => onMove(question.id, group, 1)}
            aria-label="Move question down"
            className="min-h-11 rounded-md border p-2 disabled:opacity-40"
          >
            <ArrowDown className="h-4 w-4" aria-hidden="true" />
          </button>
          {!readOnly && (
            <>
              <button
                type="button"
                disabled={busy || globalBusy}
                onClick={() => setEditing(true)}
                className="min-h-11 rounded-md border px-2 text-xs font-semibold"
              >
                Edit
              </button>
              <button
                type="button"
                disabled={busy || globalBusy}
                onClick={remove}
                aria-label="Delete question"
                className="min-h-11 rounded-md border border-red-200 p-2 text-red-700"
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </button>
            </>
          )}
        </div>
      </div>
    </article>
  );
}

function DiffStat({ label, count }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold">{count}</p>
    </div>
  );
}
function RequirementRow({
  requirement,
  index,
  total,
  readOnly,
  globalBusy = false,
  reorderDisabled = false,
  onDirtyChange,
  onMove,
  onSave,
  onDelete,
}) {
  const [busy, setBusy] = useState(false);
  const [value, setValue] = useState(requirement.label);
  const [level, setLevel] = useState(requirement.level);
  const [order, setOrder] = useState(requirement.order);
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    onDirtyChange(requirement.id, dirty);
    return () => onDirtyChange(requirement.id, false);
  }, [requirement.id, dirty, onDirtyChange]);
  useEffect(() => {
    if (readOnly) {
      setEditing(false);
      setDirty(false);
    }
  }, [readOnly]);
  useEffect(() => {
    if (!dirty) {
      setValue(requirement.label);
      setLevel(requirement.level);
      setOrder(requirement.order);
    }
  }, [requirement, dirty]);
  async function save() {
    setBusy(true);
    try {
      const result = await onSave({
        label: value,
        level: Number(level),
        order: Number(order),
      });
      if (result) {
        setEditing(false);
        setDirty(false);
      }
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card p-3">
      <span className="w-14 text-xs font-semibold text-muted-foreground">
        L{requirement.level} · {requirement.order}
      </span>
      {editing ? (
        <div className="flex min-w-0 basis-full flex-wrap gap-2 sm:flex-1 sm:basis-0">
          <input
            aria-label="Requirement label"
            disabled={busy || globalBusy}
            value={value}
            onChange={(e) => {
              setDirty(true);
              setValue(e.target.value);
            }}
            className="min-h-11 min-w-0 basis-full rounded-lg border px-3 sm:flex-1"
          />
          <input
            aria-label="Requirement level"
            disabled={busy || globalBusy}
            type="number"
            min="1"
            max="3"
            value={level}
            onChange={(e) => {
              setDirty(true);
              setLevel(e.target.value);
            }}
            className="min-h-11 w-20 rounded-lg border px-2"
          />
          <input
            aria-label="Requirement order"
            disabled={busy || globalBusy}
            type="number"
            min="0"
            value={order}
            onChange={(e) => {
              setDirty(true);
              setOrder(e.target.value);
            }}
            className="min-h-11 w-20 rounded-lg border px-2"
          />
        </div>
      ) : (
        <span className="flex-1 text-sm">{requirement.label}</span>
      )}
      {!readOnly && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy || globalBusy || reorderDisabled || index === 0}
            onClick={() => onMove(requirement.id, -1)}
            aria-label="Move requirement up"
            className="min-h-11 rounded border p-2 disabled:opacity-40"
          >
            <ArrowUp className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            disabled={busy || globalBusy || reorderDisabled || index === total - 1}
            onClick={() => onMove(requirement.id, 1)}
            aria-label="Move requirement down"
            className="min-h-11 rounded border p-2 disabled:opacity-40"
          >
            <ArrowDown className="h-4 w-4" aria-hidden="true" />
          </button>
          {editing ? (
            <>
              <button
                type="button"
                disabled={busy || globalBusy}
                onClick={save}
                className="min-h-11 rounded border px-2 text-xs font-semibold"
              >
                Save
              </button>
              <button
                type="button"
                disabled={busy || globalBusy}
                onClick={() => {
                  setEditing(false);
                  setDirty(false);
                }}
                className="min-h-11 rounded border px-2 text-xs font-semibold"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              disabled={busy || globalBusy}
              onClick={() => setEditing(true)}
              className="min-h-11 rounded border px-2 text-xs font-semibold"
            >
              Edit
            </button>
          )}
          <button
            type="button"
            disabled={busy || globalBusy}
            onClick={() => {
              const confirmation = window.prompt(
                `Type DELETE to remove “${requirement.label}”.`,
              );
              if (confirmation === "DELETE") onDelete();
            }}
            className="min-h-11 rounded border border-red-200 p-2 text-red-700"
            aria-label="Delete requirement"
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
}

export { QuestionCard, RequirementRow, DiffStat, TaskEvidenceChoices };
